import { ConditionalLink } from "components/conditionalLink";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { DocumentRequestedIcon } from "components/icons/DocumentRequestedIcon";
import { OpenItemsIcon } from "components/icons/OpenItemsIcon";
import { PaymentRequestedIcon } from "components/icons/PaymentRequestedIcon";
import { PendingDocumentsIcon } from "components/icons/PendingDocumentsIcon";
import { ReviewAndPayIcon } from "components/icons/ReviewAndPayIcon";
import { RightArrow } from "components/icons/RightArrow";
import { SensitiveDataIcon } from "components/icons/SensitiveDataIcon";
import { SignatureRequestedIcon } from "components/icons/SignatureRequestedIcon";
import TaskDetailUnarchive from "components/icons/TaskDetailUnarchive";
import { DEPRECATED, REQUESTED } from "constants/sensitiveData";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { ReactNode, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import Archive from "static/images/Archive.svg";
import NoPendingActionItems from "static/images/NoPendingActionItems.svg";
import { TaxActionItemType } from "store/apis/actionItems";
import { setMessageForStatusUpdate } from "store/slices/chat";
import { MessageResponse } from "stream-chat";
import { DefaultStreamChatGenerics, useChatContext } from "stream-chat-react";
import { ActionItemCard } from "./ActionItemCard";
import ToolTip from "components/design/toolTip";
import {
  MERCHANT_EMAIL_REQUEST_CUSTOM_TYPE,
  PRICED_TICKET_CARD,
  REQUESTED_ANSWERS_CUSTOM_TYPE,
  REQUESTED_DFT_REVIEW_TYPE,
  TICKET_CARD,
} from "constants/chatType";
import { MentionIcon } from "components/icons/MentionIcon";
import { pluralize } from "utils/pluralize";
import { ReviewDocHeader } from "components/icons/ReviewDocHeader";
import { TicketCreated } from "components/icons/TicketCreated";
import { PinnedFlag } from "components/icons/PinnedFlag";
import { RegisteredAgent } from "components/icons/Navbar/RegisteredAgent";

export const actionIcons: { [key: string]: React.JSX.Element } = {
  REQUESTED_DOCUMENTS_CUSTOM_TYPE: <DocumentRequestedIcon />,
  PAYMENT_REQUESTED: <PaymentRequestedIcon />,
  DOCUMENT_REVIEW: <PendingDocumentsIcon />,
  REVIEW_AND_PAY: <ReviewAndPayIcon />,
  SENSITIVE_DATA_CUSTOM_TYPE: <SensitiveDataIcon />,
  ESIGN_REQUEST_CUSTOM_TYPE: <SignatureRequestedIcon />,
  OPEN_ITEM_CUSTOM_TYPE: <OpenItemsIcon />,
  REQUESTED_REVIEW_DRAFT_TYPE: <DocumentRequestedIcon />,
  REQUESTED_DFT_REVIEW_TYPE: <ReviewDocHeader />,
  W_FORMS_PENDING: <DocumentRequestedIcon />,
  MERCHANT_EMAIL_REQUEST: <MentionIcon className="t-text-neutral t-size-4" />,
  PRICED_TICKET_CARD: <TicketCreated className="t-text-neutral t-size-4" />,
  STATE_REGISTRATIONS_DUE: <PinnedFlag />,
  STATE_REGISTRATION_MISSING_INFO: <ReviewDocHeader />,
  RA_EXPIRING: <RegisteredAgent />,
};

export const ActionItemRowTrigger = ({
  customType,
  itemCount,
  title,
  subtitle,
  handleCardClick,
}: {
  customType: string;
  itemCount: string | number;
  title: string;
  subtitle: string;
  handleCardClick?: () => void;
}) => (
  <div
    className="t-flex t-justify-between t-items-center t-py-4 t-px-2 t-border t-border-solid t-border-neutral-0 t-border-t-surface-transparent t-border-l-surface-transparent t-border-r-surface-transparent t-group active:t-border-purple active:t-border-t-purple active:t-border-r-purple active:t-border-l-purple t-rounded-lg hover:t-bg-surface-lighter-grey t-gap-4"
    role="button"
    onClick={handleCardClick}
  >
    <div className="t-flex t-gap-2 t-items-center">
      <div className="t-relative t-min-h-10 t-min-w-10 t-max-h-10 t-max-w-10 t-rounded-full t-flex t-items-center t-justify-center t-bg-surface-lighter-grey">
        {actionIcons[customType] || <DocumentRequestedIcon />}
      </div>
      <div>
        <div className="t-text-subtext t-text-text-100">
          {itemCount} {title}
        </div>
        <div className="t-text-body-sm t-text-text-30">{subtitle}</div>
      </div>
    </div>
    <Button size="small" customType="text">
      <div className="t-text-text-60 group-hover:t-hidden t-block">
        <RightArrow />
      </div>
      <div className="t-hidden group-hover:t-block">View</div>
    </Button>
  </div>
);

export const ActionItemRow = ({
  item,
}: {
  item: TaxActionItemType & { custom_type: string };
  isFromChat?: boolean;
}) => {
  const { ids: messageIds, title } = item;
  const actionModal = useModal();
  const { client } = useChatContext();
  const [messages, setMessages] = useState<
    MessageResponse<DefaultStreamChatGenerics>[]
  >([]);
  const { search } = useLocation();
  const { alertToast, successToast } = useToast();
  const { isAdmin } = useRoleBasedView();
  const dispatch = useDispatch();

  const getMessages = async (messageIds: string[]) => {
    try {
      const searchResults = await client.search(
        {
          members: { $in: [client.user?.id!] },
        },
        { id: { $in: messageIds } },
        {}
      );
      setMessages(
        searchResults.results
          .filter(
            (result) =>
              result.message.custom_type == TICKET_CARD ||
              result.message.custom_type == PRICED_TICKET_CARD ||
              !result.message.channel?.frozen
          )
          .map((result) => result.message)
      );
    } catch (e: any) {
      alertToast({ message: e.message });
    }
  };

  useEffect(() => {
    if (messageIds.length > 0) {
      getMessages(messageIds as unknown as string[]);
    }
  }, [JSON.stringify(messageIds)]);

  const itemCount =
    item.custom_type === REQUESTED_ANSWERS_CUSTOM_TYPE ? "" : item.count;

  const pluralizedTitle = pluralize(
    item.count,
    "Vendor email address requested",
    "Vendor email addresses requested",
    false
  );

  return (
    <Modal.Root onOpenChange={actionModal.toggle} open={actionModal.isOpen}>
      <Modal.Trigger asChild>
        <div>
          <ActionItemRowTrigger
            customType={item.custom_type}
            title={
              item.custom_type === MERCHANT_EMAIL_REQUEST_CUSTOM_TYPE
                ? pluralizedTitle
                : item.title
            }
            subtitle={item.subtitle}
            itemCount={itemCount}
          />
        </div>
      </Modal.Trigger>
      <Modal.Content useCustomOverlay>
        <Modal.Header>
          <Modal.Title>{title}</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-space-y-5">
          {messages.length === 0 ? (
            <div className="t-flex t-flex-col t-items-center t-justify-center t-h-80 t-w-full t-gap-8">
              <img src={NoPendingActionItems} alt="NoPendingActionItems" />
              <div className="t-text-subtitle t-text-text-30">
                Awesome! No pending items
              </div>
            </div>
          ) : (
            messages.map((message) => {
              const {
                channel,
                id: messageId,
                custom_data,
                custom_type,
              } = message;
              const showSeparator = channel?.name && channel?.created_by?.name;

              return (
                <div
                  key={messageId}
                  className="t-p-3 t-rounded-lg t-bg-surface-lighter-grey t-space-y-3"
                >
                  <div className="t-flex t-justify-between t-items-center">
                    <div className="t-flex t-gap-1.5 t-items-center">
                      {message?.user?.name && (
                        <div className="t-text-subtext-sm t-text-text-100">
                          {message?.user?.name}
                        </div>
                      )}
                      {showSeparator && (
                        <div className="t-text-neutral-20 t-text-caption">
                          |
                        </div>
                      )}
                      {channel?.name && (
                        <div className="t-text-body-sm t-text-text-30">
                          {channel.name}
                        </div>
                      )}
                    </div>
                    <div className="t-flex t-items-center t-gap-3">
                      {isAdmin && message.card_status === DEPRECATED && (
                        <ToolTip text="Unarchive action item">
                          <Button
                            size="small"
                            customType="ghost"
                            onClick={() =>
                              dispatch(setMessageForStatusUpdate(message))
                            }
                          >
                            <span className="t-text-text-30">
                              <TaskDetailUnarchive
                                color="currentColor"
                                strokeWidth="1"
                              />
                            </span>
                          </Button>
                        </ToolTip>
                      )}
                      {isAdmin && message.card_status === REQUESTED && (
                        <ToolTip text="Archive action item">
                          <Button
                            size="small"
                            customType="ghost"
                            onClick={() =>
                              dispatch(setMessageForStatusUpdate(message))
                            }
                          >
                            <img src={Archive} alt="Archive" />
                          </Button>
                        </ToolTip>
                      )}
                      {channel && (
                        <ConditionalLink
                          to={`chat/${channel.id}/${messageId}${search}`}
                          target="_blank"
                        >
                          <Button customType="link" size="small">
                            Go to chat
                          </Button>
                        </ConditionalLink>
                      )}
                    </div>
                  </div>
                  <ActionItemCard
                    type={custom_type as string}
                    customData={custom_data}
                    messageId={messageId}
                    channelId={channel?.id || ""}
                    card_status={(message?.card_status as string) || ""}
                  />
                </div>
              );
            })
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
